import React, { useState, useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { commonUrl, redirectUrl } from "src/config";
import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  Autocomplete,
} from "@mui/material";
import { exportToExcel } from "../utilities/excelUtils";

import PageContainer from "src/components/container/PageContainer";
import DashboardCard from "src/components/shared/DashboardCard";
import { useSnackBar } from "src/context/SnackBarContext";
import DataTable from "src/components/DataTable";
import Loader from "src/components/Loader";
import { postData } from "../utilities/api";
import useFetchData from "../utilities/useFetchData";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import { IconRefresh } from "@tabler/icons";

function NewReport() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackBar();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [billNo, setBillNo] = useState("");
  const [company, setCompany] = useState(null);
  const [logo, setLogo] = useState(null);
  const componentRef = useRef();

  // 0 = Both (Party / Phone), 1 = Party Only, 2 = Phone Only
  const [partyDisplayMode, setPartyDisplayMode] = useState(0);

  const [selectedParty, setSelectedParty] = useState("ALL");
  const [selectedPaymentType, setSelectedPaymentType] = useState("ALL");

  const paymentModes = ["ALL", "Cash", "Card", "UPI", "Bank", "Pending"];

  const { data, isLoading: masterLoading } = useFetchData(
    ["masterData"],
    ["/api/getMasterDatas"]
  );
  const partyData = data?.masterData?.data || [];

  const togglePartyDisplayMode = () => {
    setPartyDisplayMode((prevMode) => (prevMode + 1) % 3);
  };

  const filteredData = reportData.filter((row) => {
    // 1. Party Filter Match
    const partyMatch =
      selectedParty === "ALL" ||
      !selectedParty ||
      row.party?.toLowerCase() === selectedParty?.label?.toLowerCase();

    // 2. Payment Type Filter Match
    let displayType = row.paymentType || "Pending";
    if (row.amountPaid == null || Number(row.amountPaid) === 0) {
      displayType = "Pending";
    }

    const paymentMatch =
      selectedPaymentType === "ALL" ||
      !selectedPaymentType ||
      displayType.toLowerCase() === selectedPaymentType.toLowerCase();

    return partyMatch && paymentMatch;
  });

  const columns = [
    { field: "billNo", headerName: "Bill No", width: 120 },
    {
      field: "party",
      headerName:
        partyDisplayMode === 0
          ? "Party / Phone"
          : partyDisplayMode === 1
          ? "Party"
          : "Phone",
      flex: 1,
      renderHeader: () => (
        <Box
          onClick={togglePartyDisplayMode}
          sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 0.5, userSelect: "none" }}
          title="Click to toggle: Both -> Party Only -> Phone Only"
        >
          <span>
            {partyDisplayMode === 0 && "Party / Phone"}
            {partyDisplayMode === 1 && "Party"}
            {partyDisplayMode === 2 && "Phone"}
          </span>
          <IconRefresh size={14} />
        </Box>
      ),
      renderCell: (row) => {
        const name = row.party || "Cash Customer";
        const phone = row.phone ? ` (${row.phone})` : "";
        if (partyDisplayMode === 0) {
          return `${name}${phone}`;
        } else if (partyDisplayMode === 1) {
          return name;
        } else {
          return row.phone || "-";
        }
      },
    },
    { field: "del_date", headerName: "Delivery Date", flex: 1 },
    {
      field: "netTotal",
      headerName: "Net Total",
      flex: 1,
      renderCell: (row) => (
        <span>{row.netTotal != null ? Number(row.netTotal).toFixed(2) : "-"}</span>
      )
    },
    {
      field: "amountPaid",
      headerName: "Amount Paid",
      flex: 1,
      renderCell: (row) => (
        <span>{row.amountPaid != null ? Number(row.amountPaid).toFixed(2) : "-"}</span>
      )
    },
    {
      field: "paymentType",
      headerName: "Payment Type",
      flex: 1,
      renderCell: (row) => {
        let displayType = row.paymentType || "Pending";
        if (row.amountPaid == null || Number(row.amountPaid) === 0) {
          displayType = "Pending";
        }
        return <span>{displayType}</span>;
      }
    },
  ];

  useEffect(() => {
    function getCompanyDetails() {
      axios
        .get(commonUrl + "main/getCompanyDetails", { withCredentials: true })
        .then((res) => {
          if (res.data[0]) {
            setCompany(res?.data[0]);
            if (res.data[0]?.logo) {
              try {
                const parsedLogo = JSON.parse(res.data[0].logo);
                setLogo(parsedLogo.img);
              } catch (e) {
                console.error("Error parsing logo:", e);
              }
            }
          }
        })
        .catch((err) => {
          if (err.response?.status === 401 || err.response?.status === 403) {
            window.location.href = redirectUrl;
          }
        });
    }

    getCompanyDetails();
  }, []);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `NewDeliveryReport from ${fromDate?.format("DD-MM-YYYY")} to ${toDate?.format("DD-MM-YYYY")}`,
  });

  const handleExportExcel = () => {
    if (filteredData.length === 0) return;

    const partyHeaderName =
      partyDisplayMode === 0
        ? "PARTY / PHONE"
        : partyDisplayMode === 1
        ? "PARTY"
        : "PHONE";

    const dataForExcel = filteredData.map((row, index) => {
      let displayType = row.paymentType || "Pending";
      if (row.amountPaid == null || Number(row.amountPaid) === 0) {
        displayType = "Pending";
      }

      const name = row.party || "Cash Customer";
      const phone = row.phone ? ` (${row.phone})` : "";
      let partyVal = name;
      if (partyDisplayMode === 0) partyVal = `${name}${phone}`;
      else if (partyDisplayMode === 1) partyVal = name;
      else partyVal = row.phone || "-";

      return {
        "SI NO": index + 1,
        "BILL NO": row.billNo,
        [partyHeaderName]: partyVal,
        "DELIVERY DATE": row.del_date,
        "NET TOTAL": row.netTotal != null ? Number(row.netTotal).toFixed(2) : "-",
        "AMOUNT PAID": row.amountPaid != null ? Number(row.amountPaid).toFixed(2) : "-",
        "PAYMENT TYPE": displayType,
      };
    });

    const totalNetTotal = filteredData.reduce((sum, row) => sum + (row.netTotal != null ? Number(row.netTotal) : 0), 0);
    const totalAmountPaid = filteredData.reduce((sum, row) => sum + (row.amountPaid != null ? Number(row.amountPaid) : 0), 0);

    dataForExcel.push({
      "SI NO": "",
      "BILL NO": "",
      [partyHeaderName]: "TOTAL",
      "DELIVERY DATE": "",
      "NET TOTAL": totalNetTotal.toFixed(2),
      "AMOUNT PAID": totalAmountPaid.toFixed(2),
      "PAYMENT TYPE": "",
    });

    const fileName = `NewDeliveryReport_${fromDate.format("DDMMYYYY")}_${toDate.format("DDMMYYYY")}.xlsx`;
    exportToExcel(dataForExcel, fileName);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      fromDate: fromDate ? fromDate.format("YYYY-MM-DD") : null,
      toDate: toDate ? toDate.format("YYYY-MM-DD") : null,
      billNo: billNo || null,
    };

    const result = await postData("/api/getDeliveryReportss", payload);

    if (!result.success) {
      showSnackbar("error", result.message);
      setReportData([]);
    } else if (result.data?.length === 0) {
      showSnackbar("warning", "No records found");
      setReportData([]);
    } else {
      setReportData(result.data);
    }
    setLoading(false);
  };

  return (
    <>
      <Loader start={loading || masterLoading} />
      <PageContainer title="Delivery Report" description="Summary of delivered bills">
        <DashboardCard title="Delivery Report">
          <Box sx={{ mt: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2.3}>
                  <DatePicker
                    label="From Date"
                    format="DD/MM/YYYY"
                    value={fromDate}
                    onChange={(date) => setFromDate(date)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2.3}>
                  <DatePicker
                    label="To Date"
                    format="DD/MM/YYYY"
                    minDate={fromDate}
                    value={toDate}
                    onChange={(date) => setToDate(date)}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    label="Bill No"
                    size="small"
                    fullWidth
                    value={billNo}
                    onChange={(e) => setBillNo(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={1.2}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    fullWidth
                    sx={{
                      height: "40px",
                      background: "#1D8B9C",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    Submit
                  </Button>
                </Grid>
                <Grid item xs={12} md={1.2}>
                  <Button
                    variant="contained"
                    onClick={handleExportExcel}
                    disabled={filteredData.length === 0}
                    fullWidth
                    sx={{
                      height: "40px",
                      background: "#28a745",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    Excel
                  </Button>
                </Grid>
                <Grid item xs={12} md={1.2}>
                  <Button
                    variant="contained"
                    onClick={handlePrint}
                    disabled={filteredData.length === 0}
                    fullWidth
                    sx={{
                      height: "40px",
                      background: "#ef8157",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    Print
                  </Button>
                </Grid>
                <Grid item xs={12} md={1.5}>
                  <Button
                    variant="contained"
                    onClick={() => navigate("/counterBilling")}
                    fullWidth
                    sx={{
                      height: "40px",
                      background: "#1D8B9C",
                      color: "white",
                      fontWeight: "bold",
                    }}
                  >
                    Counter Bill
                  </Button>
                </Grid>
              </Grid>

              {/* Real-time Filter Controls (Party and Payment Type) */}
              {reportData.length > 0 && (
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1.5 }}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={["ALL", ...(partyData?.customer_list || [])]}
                      value={selectedParty}
                      getOptionLabel={(option) => {
                        if (typeof option === "string") return option;
                        return `${option.label || ""}-${option.name || ""}`.trim();
                      }}
                      isOptionEqualToValue={(option, value) => {
                        if (typeof option === "string" || typeof value === "string") {
                          return option === value;
                        }
                        return option.label === value.label && option.name === value.name;
                      }}
                      onChange={(e, val) => setSelectedParty(val || "ALL")}
                      renderInput={(params) => (
                        <TextField {...params} label="Filter by Party" size="small" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      size="small"
                      fullWidth
                      options={paymentModes}
                      value={selectedPaymentType}
                      getOptionLabel={(option) => option}
                      onChange={(e, val) => setSelectedPaymentType(val || "ALL")}
                      renderInput={(params) => (
                        <TextField {...params} label="Filter by Payment Type" size="small" />
                      )}
                    />
                  </Grid>
                </Grid>
              )}
            </LocalizationProvider>
          </Box>

          {/* Hidden Printable Component */}
          <div style={{ display: "none" }}>
            <div
              ref={componentRef}
              style={{
                backgroundColor: "#fff",
                padding: "25px",
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  marginBottom: "15px",
                  paddingBottom: "10px",
                  borderBottom: "2px solid #333",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {logo && (
                    <img
                      src={logo}
                      width={100}
                      alt="logo"
                      style={{ marginRight: "15px" }}
                    />
                  )}
                  <div style={{ textAlign: "left" }}>
                    <h3
                      style={{ margin: "0", fontWeight: "bold", fontSize: "22px" }}
                    >
                      {company?.Name}
                    </h3>
                    <p style={{ margin: "2px 0 0 0", fontSize: "14px" }}>
                      {company?.Add1} {company?.Place}
                    </p>
                    <p style={{ margin: "0", fontSize: "14px" }}>
                      {company?.Ph}
                    </p>
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <Typography
                    variant="h4"
                    style={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      fontSize: "24px",
                    }}
                  >
                    Delivery Report
                  </Typography>
                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                    From: {dayjs(fromDate).format("DD/MM/YYYY")} To:{" "}
                    {dayjs(toDate).format("DD/MM/YYYY")}
                  </Typography>
                </div>
              </div>

              <table
                style={{
                  width: "100%",
                  textAlign: "center",
                  fontSize: "13px",
                  borderCollapse: "collapse",
                  marginBottom: "20px",
                }}
              >
                <thead style={{ backgroundColor: "#f0f0f0" }}>
                  <tr>
                    {[
                      "SI",
                      "Bill No",
                      partyDisplayMode === 0
                        ? "Party / Phone"
                        : partyDisplayMode === 1
                        ? "Party"
                        : "Phone",
                      "Delivery Date",
                      "Net Total",
                      "Amount Paid",
                      "Payment Type",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          border: "1px solid #ccc",
                          padding: "8px",
                          fontWeight: "bold",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row, idx) => {
                    let displayType = row.paymentType || "Pending";
                    if (row.amountPaid == null || Number(row.amountPaid || 0) === 0) {
                      displayType = "Pending";
                    }

                    const name = row.party || "Cash Customer";
                    const phone = row.phone ? ` (${row.phone})` : "";
                    let partyVal = name;
                    if (partyDisplayMode === 0) partyVal = `${name}${phone}`;
                    else if (partyDisplayMode === 1) partyVal = name;
                    else partyVal = row.phone || "-";

                    return (
                      <tr key={idx} style={{ pageBreakInside: "avoid" }}>
                        <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                          {idx + 1}
                        </td>
                        <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                          {row.billNo}
                        </td>
                        <td
                          style={{
                            border: "1px solid #ccc",
                            padding: "6px",
                            textAlign: "left",
                          }}
                        >
                          {partyVal}
                        </td>
                        <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                          {row.del_date}
                        </td>
                        <td
                          style={{
                            border: "1px solid #ccc",
                            padding: "6px",
                            textAlign: "right",
                          }}
                        >
                          {Number(row.netTotal || 0).toFixed(2)}
                        </td>
                        <td
                          style={{
                            border: "1px solid #ccc",
                            padding: "6px",
                            textAlign: "right",
                          }}
                        >
                          {Number(row.amountPaid || 0).toFixed(2)}
                        </td>
                        <td style={{ border: "1px solid #ccc", padding: "6px" }}>
                          {displayType}
                        </td>
                      </tr>
                    );
                  })}
                  <tr
                    style={{
                      fontWeight: "bold",
                      backgroundColor: "#eee",
                      pageBreakInside: "avoid",
                    }}
                  >
                    <td
                      colSpan={4}
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "right",
                      }}
                    >
                      Total:
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "right",
                      }}
                    >
                      {filteredData
                        .reduce(
                          (sum, row) => sum + Number(row.netTotal || 0),
                          0
                        )
                        .toFixed(2)}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                        textAlign: "right",
                      }}
                    >
                      {filteredData
                        .reduce(
                          (sum, row) => sum + Number(row.amountPaid || 0),
                          0
                        )
                        .toFixed(2)}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ccc",
                        padding: "8px",
                      }}
                    >
                    </td>
                  </tr>
                </tbody>
              </table>
              <hr />
            </div>
          </div>

          <Box sx={{ mt: 4 }}>
            <DataTable
              columns={columns}
              rows={filteredData}
              getRowId={(row) => row.billNo}
              headerBgColor="#1D8B9C"
            />
          </Box>

          {filteredData.length > 0 && (
            <Box mt={3} p={2} border="1px solid #ccc" borderRadius={2}>
              <Grid container spacing={2} justifyContent="space-between" alignItems="center">
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" textAlign="left">
                    Total Net Amount:{" "}
                    {filteredData
                      .reduce((sum, row) => sum + Number(row.netTotal || 0), 0)
                      .toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" textAlign="right">
                    Total Delivered Paid Amount:{" "}
                    {filteredData
                      .reduce((sum, row) => sum + Number(row.amountPaid || 0), 0)
                      .toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DashboardCard>
      </PageContainer>
    </>
  );
}

export default NewReport;
